import React, { useState } from "react";

import { useTranslation } from "react-i18next";

import { useTheme, Divider, Stack } from "@mui/material";

import HistogramAggSelector from "components/widgetParts/HistogramAggSelector";
import { Agg } from "system/query/Aggs";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SparklinePreview from "./SparklinePreview";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsColorPicker from 'components/settingsConfig/uiComponents/SettingsColorPicker';
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import SettingsSlider from "components/settingsConfig/uiComponents/SettingsSlider";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { QueryField, QueryFieldTypes, QueryInfo, socialUniverseField } from "system/query/QueryInfo";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest, WidgetRequestInit } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";

const defaultWidgetRequest: WidgetRequest = {
  name: 'sparkline',
  endpoint: 'query/agg',
  queryParams: {
    flatten: true,
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE,
};

export const defaultValueRequest: WidgetRequest = {
  name: "sparkline-value",
  endpoint: 'query/agg',
  queryParams: {
    flatten: true,
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
};

function buildDefaultHistogramRequest(queryInfo: QueryInfo): WidgetRequest | null {
  const requestInitBody = {
    aggs: {}
  };

  const foundTimestampField = queryInfo.fields?.find((field:QueryField) => field.type === QueryFieldTypes.timestamp) || false;
  if (foundTimestampField) {
    const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;
    requestInitBody.aggs = { 
      histogram: {
        op: "histogram",
        field: foundTimestampField.id,
        ...(foundSocialUniverseField && {
          aggs: {
            [socialUniverseField.label]: {
              op: socialUniverseField.id
            }
          }
        }),
      },
    };
  }

  const histogramRequest: WidgetRequest = { ...defaultWidgetRequest };
  histogramRequest.requestInit = { body: requestInitBody };

  return histogramRequest;  
}

function buildDefaultValueRequest(queryInfo: QueryInfo): WidgetRequest | null {
  const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;
  const requestInitBody = { aggs: {} };

  if (foundSocialUniverseField) {
    requestInitBody.aggs = {
      [socialUniverseField.label]: {
        op: socialUniverseField.id
      }
    };
  }

  const valueRequest: WidgetRequest = { ...defaultValueRequest };
  valueRequest.requestInit = { body: requestInitBody };

  return valueRequest;
}

export function BuildDefaultSparklineRequests(queryInfo: QueryInfo): any {
  const histogramRequest = buildDefaultHistogramRequest(queryInfo);
  if (histogramRequest) {
    const valueRequest = buildDefaultValueRequest(queryInfo);
    return [histogramRequest, valueRequest]
  } else {
    return [];
  }
}

export type SparklineSettingsStateType = {
  customWidgetTitle?: string;
  dotSize?: number;
  lineColor?: string;
  showDots?: boolean;
  showDotMargin?: boolean;
  showLabel?: boolean;
  showPercentChange?: boolean;
  showTotal?: boolean;
  showXAxis?: boolean;
  widgetTitle?: string;
  widgetTitleAlignment?: string;
};

export const defaultSettings: SparklineSettingsStateType = {
  customWidgetTitle: '',
  dotSize: 3,
  lineColor: undefined,
  showDots: false,
  showDotMargin: false,
  showLabel: true,
  showPercentChange: true,
  showTotal: true,
  showXAxis: false,
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
};

export default function SparklineSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'sparkline');
  const requestBody = originalRequest?.requestInit?.body || {};

  const updatedDefaultSettings = { ...defaultSettings, ...props?.widget?.data?.settings };
  const [settingsState, setSettingsState] = useState<SparklineSettingsStateType>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: SparklineSettingsStateType) => setSettingsState(updatedState);

  const [localAggs, setLocalAggs] = useState<any>(requestBody?.aggs || {});

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

  const updateWidget = () => {
    const histogramRequest: WidgetRequest = { ...defaultWidgetRequest };
    const histogramRequestInitBody = { aggs: {} };
    const valueRequest: WidgetRequest = { ...defaultValueRequest };
    const valueRequestInitBody = { aggs: {} };

    if (localAggs) {
      histogramRequestInitBody.aggs = localAggs;
      let fieldLabel = Object.keys(localAggs.histogram.aggs || {})?.[0];
      if (fieldLabel) {
        let copy = {
          ...localAggs.histogram.aggs[fieldLabel]
        };
        valueRequestInitBody.aggs = {
          [fieldLabel]: copy
        };
      }
    }

    const hasAgg = Object.keys(histogramRequestInitBody.aggs).length;
    if (hasAgg) {
      histogramRequest.requestInit = { body: histogramRequestInitBody };
      valueRequest.requestInit = { body: valueRequestInitBody };
    }

    props.saveWidgetDataAndRequests({settings: settingsState}, [histogramRequest, valueRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    })
  }

  // check if the data object has the fields needed
  // const updateAggs = () => {
  //   props.onUpdateWidgetData(props.widget?.id, {
  //     settings: settingsState
  //   }).then(() => {
  //     if (props.widgetRefresh) {
  //       props.widgetRefresh();
  //     }
  //   })

  //   const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };
  //   const requestInitBody = { aggs: {} };
  //   if (localAggs) {
  //     requestInitBody.aggs = localAggs;
  //   }
  //   if (Object.keys(requestInitBody.aggs).length) {
  //     widgetRequest.requestInit = { body: requestInitBody };
  //     props.saveWidgetRequests([widgetRequest]);
  //   } else {
  //     props.saveWidgetRequests([]);
  //   }
  // }
  const chartColors: string[] = [
    theme.colors.namedColors.chart1,
    theme.colors.namedColors.chart2,
    theme.colors.namedColors.chart3,
    theme.colors.namedColors.chart4,
    theme.colors.namedColors.chart5,
    theme.colors.namedColors.chart6,
    theme.colors.namedColors.chart7,
    theme.colors.namedColors.chart8,
    theme.colors.namedColors.chart9,
  ];

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.sparkline.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (
            <HistogramAggSelector
              aggs={localAggs?.histogram}
              queryInfo={props.queryInfo}
              setAggs={(aggs:Agg) => {
                setLocalAggs({histogram: aggs});
              }} />
          )}
        </>)}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
          {/* Widget Graph Preview */}
          <SparklinePreview settingsState={settingsState} />
          {/* Settings Options */}
          <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
            <SettingsAccordion label={i18n.t("widget-settings.widget-title")}>
              <SettingsWidgetTitleSelect
                keyid="widgetTitle"
                label={i18n.t("title")}
                showQueryOption={true}
                widgetTitleValue={settingsState.widgetTitle || ""} />
              {settingsState.widgetTitle === 'custom' &&
                <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                  <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                  <SettingsTextField
                    keyid="customWidgetTitle"
                    label={i18n.t("widget-settings.custom-text")}
                    value={settingsState.customWidgetTitle}
                  />
                </Stack>}
              <SettingsToggleButton
                keyid="widgetTitleAlignment"
                label={i18n.t("widget-settings.alignment")}
                optionsList={horizontalAlignmentOptions}
                value={settingsState.widgetTitleAlignment} />
            </SettingsAccordion>
            <SettingsAccordion label={i18n.t("graph")}>
              <SettingsColorPicker
                defaultColor={chartColors[0]}
                disableAlpha={true}
                color={settingsState.lineColor ?? ''}
                keyid='lineColor'
                label={i18n.t("widget-settings.line-color")} />
              <SettingsCheckbox
                keyid='showDots'
                label={i18n.t("widget-settings.show-dots")}
                checked={settingsState.showDots} />
                {settingsState.showDots &&
                  <>
                    <SettingsSlider
                      keyid={'dotSize'}
                      label={i18n.t("widget-types.sparkline.settings.dot-size")}
                      sliderValue={settingsState.dotSize ?? 5}
                      defaultValue={3}
                      min={1}
                      max={10} />
                    <SettingsCheckbox
                      keyid="showDotMargin"
                      label="Show Dot Margin"
                      checked={settingsState.showDotMargin}
                    />
                  </>
                }
              <SettingsCheckbox
                keyid='showTotal'
                label={i18n.t("widget-types.sparkline.settings.show-total")}
                checked={settingsState.showTotal} />
              <SettingsCheckbox
                keyid='showPercentChange'
                label={i18n.t("widget-types.sparkline.settings.show-percent-change")}
                checked={settingsState.showPercentChange} />
              <SettingsCheckbox
                keyid="showXAxis"
                label="Show X-Axis"
                checked={settingsState.showXAxis}
              />
            </SettingsAccordion>
          </SettingsOptions>
        </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  );
}
